import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Menu, MenuItem } from "@mui/material";
import { ArrowBack, Close, Download } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleChevronUp, faCircleChevronDown, faCircleChevronRight, faFileLines, faAngleUp, faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { getAllConfiguartions } from "../../services/updateMetaConfigService";
import { getUserRolesBasedOnAccess, isRoleConfigEnabled } from "../../services/uiConfigService";
import { getReportConfig } from "../../services/mdmService/mdmService";
import dayjs, { Dayjs } from "dayjs";
import MdmReportsPreview from "./MdmReportsPreview";
import "./MdmReportsFilter.css";
import TopFilterBar from "./components/TopFilterBar";
import { newReportConfig } from "./mdmReportsUtils";
import { useHierarchyLoaders } from "./hooks/useHierarchyLoaders";
import { DrillDownPathItem, PathSummaryItem, summarizeDrillDownPath } from "./utils/hierarchyHelpers";
import { 
  fetchFilterValues,
  FilterOption,
  fetchUsersByDesignation,
  fetchLocationUsers,
  fetchChildrenUsers
} from "../../services/reportsDataService";
import { 
  loadCustomFiltersForReport,
  isMergedFilterForReport,
  getMergedFilterSources
} from "../../services/mdmCustomFiltersService";
import { buildLocationFilters, buildUserFilters, collectDistributorCodes, handleMdmReportDownload } from "../../services/mdmReportsDownloadService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { fetchDistributorMeta, DistributorFeature, filterDistributorsBySelections } from "../../services/distributorMetaService";
import DateRangeSelector from "./NewDateFilter";
import { ConfirmationPopUp } from "../../components/popup/confirmationPopUp";
import { Loader } from "../../components/loader/Loader";

type DistributorSource = "geographical" | "sales";

const resolveDefaultDistributorSource = (report?: newReportConfig | null): DistributorSource => {
  if (report?.geographicalHierarchyFilter?.enabled) {
    return "geographical";
  }
  if (report?.salesHierarchyFilter?.enabled) {
    return "sales";
  }
  return "geographical";
};

type ReportItem = {
  id: string;
  name: string;
  type: string;
  filter?: string;
  isNewUi?: boolean;
  isNewReportUi?: boolean;
  newReportConfig?: newReportConfig;
};

type ReportGroup = {
  type: string;
  name: string;
  children: ReportItem[];
};

const getValuesFromPath = (path: DrillDownPathItem[], level: string): string[] => {
  if (!path || path.length === 0) {
    return [];
  }
  const values = path.filter(item => item.level === level).map(item => item.value);
  return values;
};

const buildHierarchyPathUpToLevel = (
  existingPath: DrillDownPathItem[],
  filters: Record<string, string[]>,
  hierarchyOrder: string[] | undefined,
  uptoLevel?: string | null
): DrillDownPathItem[] => {
  if (!uptoLevel) {
    return existingPath;
  }

  let orderedLevels: string[] = [];
  if (hierarchyOrder && hierarchyOrder.length > 0) {
    orderedLevels = [...hierarchyOrder];
  }

  if (!orderedLevels.includes(uptoLevel)) {
    orderedLevels.push(uptoLevel);
  }

  const newPath: DrillDownPathItem[] = [];

  for (const level of orderedLevels) {
    const pendingValues = level in filters
      ? filters[level]
      : getValuesFromPath(existingPath, level);

    if (pendingValues && pendingValues.length > 0) {
      pendingValues.forEach(value => newPath.push({ level, value }));
    }

    if (level === uptoLevel) {
      break;
    }
  }

  return newPath;
};

export default function ReportsFilterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const initialReport = location.state?.reportProps || null;
  const [groupedReports, setGroupedReports] = useState<ReportGroup[]>([]);
  const [currentReport, setCurrentReport] = useState<ReportItem | null>(initialReport);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openType, setOpenType] = useState<string | null>(null);
  const isDistributorView = currentReport?.newReportConfig?.isDistributorView || false;
  
  const getInitialDates = () => {
    const today = dayjs();
    return {
      fromDate: today,
      toDate: today
    };
  };
  
  const initialDates = getInitialDates();
  const [fromDate, setFromDate] = useState<Dayjs>(initialDates.fromDate);
  const [toDate, setToDate] = useState<Dayjs>(initialDates.toDate);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [optionsMap, setOptionsMap] = useState<Record<string, { label: string; value: string }[]>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [salesOptionsCache, setSalesOptionsCache] = useState<Record<string, { label: string; value: string }[]>>({});
  const [geoOptionsCache, setGeoOptionsCache] = useState<Record<string, { label: string; value: string }[]>>({});
  const [distributorMetaOptionCache, setDistributorMetaOptionCache] = useState<{
    types: { label: string; value: string }[];
    divisions: { label: string; value: string }[];
  }>({ types: [], divisions: [] });
  const [salesDrillDownPath, setSalesDrillDownPath] = useState<DrillDownPathItem[]>([]);
  const [geoDrillDownPath, setGeoDrillDownPath] = useState<DrillDownPathItem[]>([]);
  const [geoHierarchyOrder, setGeoHierarchyOrder] = useState<string[]>([]);
  const [customFilters, setCustomFilters] = useState<FilterOption[]>([]);
  const [searchTextMap, setSearchTextMap] = useState<Record<string, string>>({});
  const [distributorFeatures, setDistributorFeatures] = useState<DistributorFeature[]>([]);
  const [customFiltersLoading, setCustomFiltersLoading] = useState<boolean>(false);
  const [customFilterSelectionOrder, setCustomFilterSelectionOrder] = useState<string[]>([]);
  const selectedReport = currentReport?.newReportConfig ?? null;
  const distributorFieldKey = selectedReport?.distributorFilter?.field;
  const [distributorSource, setDistributorSource] = useState<DistributorSource>(
    resolveDefaultDistributorSource(selectedReport)
  );
  const timeoutRefs = React.useRef<Record<string, NodeJS.Timeout>>({});
  const [showSourceChangeConfirm, setShowSourceChangeConfirm] = useState(false);
  const pendingSourceChange = React.useRef<DistributorSource | null>(null);
  const [dateFilterKey, setDateFilterKey] = useState(0);
  const [isHierarchySyncing, setIsHierarchySyncing] = useState(false);
  const salesApplyJustClicked = React.useRef(false);
  const geoApplyJustClicked = React.useRef(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadLoader, setDownloadLoader] = useState(false);

  const {
    loadSalesLevels,
    loadSalesValues,
    loadGeographicalLevels,
    loadGeographicalValues,
  } = useHierarchyLoaders({
    selectedReport,
    filters,
    setOptionsMap,
    setLoadingMap,
    salesDrillDownPath,
    geoDrillDownPath,
    geoHierarchyOrder,
    setGeoHierarchyOrder,
    salesOptionsCache,
    setSalesOptionsCache,
    geoOptionsCache,
    setGeoOptionsCache,
  });

  const isGeoLastLevel = useMemo(() => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled || geoHierarchyOrder.length === 0) {
      return false;
    }
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (!currentLevel) return false;

    const lastLevel = geoHierarchyOrder[geoHierarchyOrder.length - 1];
    return currentLevel === lastLevel;
  }, [selectedReport?.geographicalHierarchyFilter, geoHierarchyOrder, filters]);

  const salesPathSummary = useMemo(
    () => summarizeDrillDownPath(salesDrillDownPath),
    [salesDrillDownPath]
  );

  const geoPathSummary = useMemo(
    () => summarizeDrillDownPath(geoDrillDownPath),
    [geoDrillDownPath]
  );

  const clearDistributorSelections = useCallback(() => {
    if (!selectedReport?.distributorFilter?.field) {
      return;
    }
    const distributorFieldKey = selectedReport.distributorFilter.field;
    setFilters(prev => {
      if (!prev[distributorFieldKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[distributorFieldKey];
      
      customFilters.forEach(filter => {
        const filterKey = filter.alias;
        if (updated[filterKey]) {
          delete updated[filterKey];
        }
      });
      
      return updated;
    });
    setOptionsMap(prev => {
      if (!prev[distributorFieldKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[distributorFieldKey];
      
      customFilters.forEach(filter => {
        delete updated[filter.alias];
      });
      
      return updated;
    });
    setSearchTextMap(prev => {
      if (!prev[distributorFieldKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[distributorFieldKey];
      
      customFilters.forEach(filter => {
        delete updated[filter.alias];
      });
      
      return updated;
    });
  }, [selectedReport?.distributorFilter?.field, customFilters]);

  const clearSalesHierarchySelections = useCallback(() => {
    if (!selectedReport?.salesHierarchyFilter) return;
    
    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[levelKey];
      hierarchyOrder.forEach(level => {
        delete newFilters[level];
      });
      return newFilters;
    });
    
    setSalesDrillDownPath([]);
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      hierarchyOrder.forEach(level => {
        delete updated[level];
      });
      return updated;
    });
    setSalesOptionsCache({});
  }, [selectedReport?.salesHierarchyFilter]);

  const clearGeoHierarchySelections = useCallback(() => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[levelKey];
      geoHierarchyOrder.forEach(level => {
        delete newFilters[level];
      });
      return newFilters;
    });
    
    setGeoDrillDownPath([]);
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      geoHierarchyOrder.forEach(level => {
        delete updated[level];
      });
      return updated;
    });
    setGeoOptionsCache({});
  }, [selectedReport?.geographicalHierarchyFilter, geoHierarchyOrder]);

  const hasSalesSelections = useCallback(() => {
    if (salesDrillDownPath.length > 0) return true;
    if (!selectedReport?.salesHierarchyFilter) return false;
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    return hierarchyOrder.some(level => filters[level] && filters[level].length > 0);
  }, [salesDrillDownPath, filters, selectedReport?.salesHierarchyFilter]);

  const hasGeoSelections = useCallback(() => {
    if (geoDrillDownPath.length > 0) return true;
    if (!selectedReport?.geographicalHierarchyFilter) return false;
    return geoHierarchyOrder.some(level => filters[level] && filters[level].length > 0);
  }, [geoDrillDownPath, filters, selectedReport?.geographicalHierarchyFilter, geoHierarchyOrder]);

  useEffect(() => {
    if (selectedReport?.salesHierarchyFilter?.enabled) {
      loadSalesLevels();
    }
  }, [selectedReport?.salesHierarchyFilter?.enabled, loadSalesLevels]);

  useEffect(() => {
    if (selectedReport?.geographicalHierarchyFilter?.enabled) {
      loadGeographicalLevels();
    }
  }, [selectedReport?.geographicalHierarchyFilter?.enabled, loadGeographicalLevels]);

  // Helper function to get source alias from merged filter
  const getSourceAliasFromMergedFilter = useCallback((mergedFilterAlias: string, value: string): string | undefined => {
    const mergedSources = getMergedFilterSources(mergedFilterAlias, selectedReport);
    return mergedSources?.find(s => s.value === value)?.alias;
  }, [selectedReport]);

  const handleFilterChange = (key: string, value: string | null) => {
    setFilters(prev => ({
      ...prev,
      [key]: value ? [value] : []
    }));
  };

  const handleMultiFilterChange = (key: string, values: string[]) => {
    if (isMergedFilter(key)) {
      handleMergedFilterChange(key, values);
      return;
    }

    const shouldClearDistributor = 
      (key === 'distributor_type' || key === 'distributor_division') && 
      values.length === 0 &&
      selectedReport?.distributorFilter?.enabled;
    
    const distributorFilterField = selectedReport?.distributorFilter?.field;
    const hierarchyOrder = selectedReport?.salesHierarchyFilter?.hierarchyOrder || [];
    const supplierLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : 'supplier';
    
    const isDistributorChange = key === distributorFilterField || key === supplierLevel ||key === 'distributor_type' || key === 'distributor_division';
    const isCustomFilter = customFilters.some(cf => cf.alias === key);
    const hadSelections = filters[key] && filters[key].length > 0 && filters[key][0] !== "";
    const hasSelections = values.length > 0 && values[0] !== "";
    
    let updatedSelectionOrder = [...customFilterSelectionOrder];
    
    if (isCustomFilter) {
      if (!hasSelections && hadSelections) {
        updatedSelectionOrder = updatedSelectionOrder.filter(filterKey => filterKey !== key);
      } 
      else if (hasSelections && !hadSelections) {
        if (!updatedSelectionOrder.includes(key)) {
          updatedSelectionOrder = [...updatedSelectionOrder, key];
        }
      }
      else if (hasSelections && hadSelections) {
        const currentIndex = updatedSelectionOrder.indexOf(key);
        if (currentIndex === -1) {
          updatedSelectionOrder = [...updatedSelectionOrder, key];
        }
      }
    }
    
    const filtersToClear: string[] = [];
    if (isCustomFilter && hasSelections) {
      const currentIndex = customFilterSelectionOrder.indexOf(key);
      if (currentIndex !== -1) {
        filtersToClear.push(...customFilterSelectionOrder.slice(currentIndex + 1));
      }
    }else if (isCustomFilter && !hasSelections && hadSelections) {
      const currentIndex = customFilterSelectionOrder.indexOf(key);
      if (currentIndex !== -1) {
        filtersToClear.push(...customFilterSelectionOrder.slice(currentIndex + 1));
      }
    }
    
    setCustomFilterSelectionOrder(updatedSelectionOrder);
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: values
      };
      
      if (shouldClearDistributor && distributorFilterField) {
        if (newFilters[distributorFilterField]) {
          delete newFilters[distributorFilterField];
        }
      }
      
      if (isDistributorChange) {
        customFilters.forEach(filter => {
          if (newFilters[filter.alias]) {
            delete newFilters[filter.alias];
          }
        });
        clearMergedFilters();
      }
      
      if (filtersToClear.length > 0) {
        filtersToClear.forEach(dependentKey => {
          if (newFilters[dependentKey]) {
            delete newFilters[dependentKey];
          }
        });
      }
      
      return newFilters;
    });

    // If a custom filter changed, invalidate cached options for other custom filters
    // that currently have no selections so they reload with the latest dependencies.
    if (isCustomFilter) {
      const nextFilters = { ...filters, [key]: values };
      const filtersToInvalidate = customFilters
        .map(cf => cf.alias)
        .filter(alias => alias !== key)
        .filter(alias => {
          const vals = nextFilters[alias];
          return !vals || vals.length === 0 || vals[0] === "";
        });

      if (filtersToInvalidate.length > 0) {
        setFilters(prev => {
          const updated = { ...prev };
          filtersToInvalidate.forEach(alias => {
            if (updated[alias] && updated[alias].length > 0) {
              delete updated[alias];          // clear the selections
            }
          });
          return updated;
        });
        setOptionsMap(prev => {
          const updated = { ...prev };
          filtersToInvalidate.forEach(alias => {
            delete updated[alias];
          });
          return updated;
        });

        setSearchTextMap(prev => {
          const updated = { ...prev };
          filtersToInvalidate.forEach(alias => {
            delete updated[alias];
          });
          return updated;
        });
      }
    }
    
    if (filtersToClear.length > 0) {
      setOptionsMap(prevOptions => {
        const updatedOptions = { ...prevOptions };
        filtersToClear.forEach(dependentKey => {
          delete updatedOptions[dependentKey];
        });
        return updatedOptions;
      });
      
      setSearchTextMap(prevSearch => {
        const updatedSearch = { ...prevSearch };
        filtersToClear.forEach(dependentKey => {
          delete updatedSearch[dependentKey];
        });
        return updatedSearch;
      });
    }
    
    if (isDistributorChange) {
      setCustomFilterSelectionOrder([]);
      setOptionsMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
      
      setSearchTextMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
    }
    
    if (shouldClearDistributor && distributorFilterField) {
      setOptionsMap(prev => {
        if (!prev[distributorFilterField]) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[distributorFilterField];
        return updated;
      });
      setSearchTextMap(prev => {
        if (!prev[distributorFilterField]) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[distributorFilterField];
        return updated;
      });
    }
  };

  const handleFilterOpen = (key: string) => {
    const distributorFieldKey = selectedReport?.distributorFilter?.field;
    if (key === distributorFieldKey) {
      return;
    }
    
    if (key.endsWith('_dynamic')) {
      const mergedFilterAlias = key.replace('_dynamic', '');
      if (isMergedFilter(mergedFilterAlias)) {
        const mergedFilterSelections = filters[mergedFilterAlias] || [];
        if (mergedFilterSelections.length > 0) {
          const selectedValue = mergedFilterSelections[0];
          const sourceAlias = getSourceAliasFromMergedFilter(mergedFilterAlias, selectedValue);
          if (sourceAlias && (!optionsMap[sourceAlias] || optionsMap[sourceAlias].length === 0)) {
            loadFilterOptions(sourceAlias);
          } else {
          }
        }
      }
      return;
    }
    
    if (isMergedFilter(key)) {
      const hasOptions = optionsMap[key] && optionsMap[key].length > 0;
      if (!hasOptions) {
        loadMergedFilterOptions(key);
      } else {
      }
      return;
    }
    
    const hasSelections = filters[key] && filters[key].length > 0 && filters[key][0] !== "";
    const hasOptions = optionsMap[key] && optionsMap[key].length > 0;
    if (!hasSelections && !hasOptions) {
      loadFilterOptions(key);
    } else if (hasSelections) {
    } else {
    }
  };

  const handleFilterInputChange = (key: string, inputValue: string) => {
    const distributorFieldKey = selectedReport?.distributorFilter?.field;
    if (key === distributorFieldKey) {
      return;
    }
    
    let actualKey = key;
    if (key.endsWith('_dynamic')) {
      const mergedFilterAlias = key.replace('_dynamic', '');
      if (isMergedFilter(mergedFilterAlias)) {
        const mergedFilterSelections = filters[mergedFilterAlias] || [];
        if (mergedFilterSelections.length > 0) {
          const selectedValue = mergedFilterSelections[0];
          const sourceAlias = getSourceAliasFromMergedFilter(mergedFilterAlias, selectedValue);
          if (sourceAlias) {
            actualKey = sourceAlias;
          }
        }
      }
    }
    
const hasSelections = key.endsWith('_dynamic') 
  ? (filters[key] && filters[key].length > 0 && filters[key][0] !== "")
  : (filters[actualKey] && filters[actualKey].length > 0 && filters[actualKey][0] !== "");
    const isSearchCleared = !inputValue || inputValue.trim() === "";
    
    setSearchTextMap(prev => ({ ...prev, [key]: inputValue }));
    
    // If search is cleared and there are no selections, don't make API call
    if (isSearchCleared && !hasSelections) {
      if (timeoutRefs.current[actualKey]) {
        clearTimeout(timeoutRefs.current[actualKey]);
        delete timeoutRefs.current[actualKey];
      }
      return;
    }
    
    if (hasSelections && isSearchCleared) {
      if (timeoutRefs.current[actualKey]) {
        clearTimeout(timeoutRefs.current[actualKey]);
        delete timeoutRefs.current[actualKey];
      }
      return;
    }
    
    if (timeoutRefs.current[actualKey]) {
      clearTimeout(timeoutRefs.current[actualKey]);
      delete timeoutRefs.current[actualKey]; 
    }
    const timeoutId = setTimeout(() => {
      delete timeoutRefs.current[actualKey];
      
      if (isMergedFilter(actualKey)) {
        loadMergedFilterOptions(actualKey, inputValue);
      } else {
        loadFilterOptions(actualKey, inputValue);
      }
    }, 350);
    timeoutRefs.current[actualKey] = timeoutId;
  };

  const handleDistributorSourceChange = useCallback((source: DistributorSource) => {
    if (source === distributorSource) {
      return;
    }
    
    const hasOtherSelections = source === "geographical" 
      ? hasSalesSelections() 
      : hasGeoSelections();
    
    if (hasOtherSelections) {
      pendingSourceChange.current = source;
      setShowSourceChangeConfirm(true);
    } else {
      setDistributorSource(source);
      clearDistributorSelections();
      if (selectedReport?.distributorFilter?.field) {
        setSearchTextMap(prev => {
          const updated = { ...prev };
          delete updated[selectedReport.distributorFilter!.field];
          return updated;
        });
      }
    }
  }, [distributorSource, clearDistributorSelections, hasSalesSelections, hasGeoSelections, selectedReport?.distributorFilter?.field]);

  const handleConfirmSourceChange = useCallback(() => {
    if (!pendingSourceChange.current) return;
    
    const newSource = pendingSourceChange.current;
    
    if (newSource === "geographical") {
      clearSalesHierarchySelections();
    } else {
      clearGeoHierarchySelections();
    }
    
    setDistributorSource(newSource);
    clearDistributorSelections();
    if (selectedReport?.distributorFilter?.field) {
      setSearchTextMap(prev => {
        const updated = { ...prev };
        delete updated[selectedReport.distributorFilter!.field];
        return updated;
      });
    }
    
    pendingSourceChange.current = null;
  }, [clearSalesHierarchySelections, clearGeoHierarchySelections, clearDistributorSelections, selectedReport?.distributorFilter?.field]);

  const handleCancelSourceChange = useCallback(() => {
    pendingSourceChange.current = null;
    setShowSourceChangeConfirm(false);
  }, []);

  const handleSalesLevelChange = (value: string | null) => {
    if (!selectedReport?.salesHierarchyFilter) return;

    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    const prevLevel = filters[levelKey]?.[0];
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (value) {
        newFilters[levelKey] = [value];
        const valueIndex = hierarchyOrder.indexOf(value);
        let isNavigatingUp = false;
        if (valueIndex !== -1) {
          if (prevLevel) {
            const prevLevelIndex = hierarchyOrder.indexOf(prevLevel);
            if (prevLevelIndex !== -1 && prevLevelIndex > valueIndex) {
              isNavigatingUp = true;
            }
          }
          
          if (!isNavigatingUp) {
            for (let i = valueIndex + 1; i < hierarchyOrder.length; i++) {
              const childLevel = hierarchyOrder[i];
              if (prev[childLevel] && prev[childLevel].length > 0) {
                isNavigatingUp = true;
                break;
              }
            }
          }
        }
        let hasValidParentContext = valueIndex === 0 || isNavigatingUp;
        if (!hasValidParentContext && valueIndex > 0) {
          if (salesDrillDownPath.length > 0) {
            hasValidParentContext = salesDrillDownPath.some(item => {
              const itemIndex = hierarchyOrder.indexOf(item.level);
              return itemIndex !== -1 && itemIndex < valueIndex;
            });
          }
          
          if (!hasValidParentContext) {
            for (let i = 0; i < valueIndex; i++) {
              const parentLevel = hierarchyOrder[i];
              if (prev[parentLevel] && prev[parentLevel].length > 0) {
                hasValidParentContext = true;
                break;
              }
            }
          }
        }
        
        const committedValues = getValuesFromPath(salesDrillDownPath, value);
        const currentValues = prev[value] || [];
        if (currentValues.length > 0) {
          newFilters[value] = [...currentValues];
        } else if (committedValues.length > 0) {
          newFilters[value] = [...committedValues];
        } else if (!hasValidParentContext) {
          if (newFilters[value]) {
            delete newFilters[value];
          }
        }
      } else {
        delete newFilters[levelKey];
      }
      return newFilters;
    });
    
    const hasCache = value ? (salesOptionsCache[value] && salesOptionsCache[value].length > 0) : false;
    const hasOptionsInMap = value ? (optionsMap[value] && optionsMap[value].length > 0) : false;
    const shouldLoad = value && (!hasOptionsInMap || (prevLevel !== value && !hasCache));
    
    if (shouldLoad) {
      loadSalesValues(value);
    } else if (value && hasCache && !hasOptionsInMap) {
      setOptionsMap(prev => ({ ...prev, [value]: salesOptionsCache[value] }));
    }
    
    if (selectedReport.distributorFilter?.enabled && selectedReport.distributorFilter?.field) {
      setSearchTextMap(prev => {
        const updated = { ...prev };
        delete updated[selectedReport.distributorFilter!.field];
        return updated;
      });
    }
  };

  const handleSalesValueChange = (values: string[]) => {
    if (!selectedReport?.salesHierarchyFilter) return;
    
    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (!currentLevel) return;
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    const currentLevelIndex = hierarchyOrder.indexOf(currentLevel);
    
    if (currentLevelIndex !== -1) {
      setOptionsMap(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        return updated;
      });

      setSalesOptionsCache(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        return updated;
      });
    }
    
    setFilters(prev => ({
      ...prev,
      [currentLevel]: values
    }));
    
  };

  const handleSalesNextLevel = () => {
    if (!selectedReport?.salesHierarchyFilter) return;
    
    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (!currentLevel) return;
    
    setIsHierarchySyncing(true);
    salesApplyJustClicked.current = true;
    
    const updatedPath = buildHierarchyPathUpToLevel(
      salesDrillDownPath,
      filters,
      selectedReport.salesHierarchyFilter?.hierarchyOrder || [],
      currentLevel
    );
    setSalesDrillDownPath(updatedPath);
    
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    const currentLevelIndex = hierarchyOrder.indexOf(currentLevel);
    const supplierLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : 'supplier';
    const isSupplierLevelChange = currentLevel === supplierLevel;
    
    if (currentLevelIndex !== -1) {
      setOptionsMap(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        if (isSupplierLevelChange) {
          customFilters.forEach(filter => {
            delete updated[filter.alias];
          });
        }
        return updated;
      });

      setSalesOptionsCache(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        return updated;
      });
      
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete newFilters[hierarchyOrder[i]];
        }
        
        if (isSupplierLevelChange) {
          customFilters.forEach(filter => {
            delete newFilters[filter.alias];
          });
          Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
            delete newFilters[mergedFilterAlias];
            const dynamicKey = `${mergedFilterAlias}_dynamic`;
            delete newFilters[dynamicKey];
          });
        }
        return newFilters;
      });
      
      if (isSupplierLevelChange) {
        setSearchTextMap(prev => {
          const updated = { ...prev };
          customFilters.forEach(filter => {
            delete updated[filter.alias];
          });
          Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
            delete updated[mergedFilterAlias];
            const dynamicKey = `${mergedFilterAlias}_dynamic`;
            delete updated[dynamicKey];
          });
          return updated;
        });
      }
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        return newFilters;
      });
    }
    loadSalesLevels();
  };

  const handleSalesPathReset = (level: string, startIndex: number) => {
    if (!selectedReport?.salesHierarchyFilter) return;
    
    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    
    setSalesDrillDownPath(prev => {
      const newPath = prev.slice(0, startIndex);
      return newPath;
    });

    setFilters(prev => {
      const newFilters = { ...prev };
      
      delete newFilters[levelKey];
      
      const supplierLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : 'supplier';
      const isSupplierLevelCleared = hierarchyOrder.length > 0 
        ? hierarchyOrder.indexOf(level) <= hierarchyOrder.indexOf(supplierLevel)
        : false;
      
      if (hierarchyOrder.length > 0) {
        const levelIndex = hierarchyOrder.indexOf(level);
        const levelsToDelete: string[] = [];
        hierarchyOrder.forEach((hierLevel, idx) => {
          if (levelIndex !== -1 && idx >= levelIndex) {
            delete newFilters[hierLevel];
            levelsToDelete.push(hierLevel);
          }
        });
      } else {
        delete newFilters[level];
      }
      
      if (isSupplierLevelCleared) {
        customFilters.forEach(filter => {
          delete newFilters[filter.alias];
        });
        Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
          delete newFilters[mergedFilterAlias];
          const dynamicKey = `${mergedFilterAlias}_dynamic`;
          delete newFilters[dynamicKey];
        });
      }
      
      return newFilters;
    });
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      if (hierarchyOrder.length > 0) {
        const levelIndex = hierarchyOrder.indexOf(level);
        hierarchyOrder.forEach((hierLevel, idx) => {
          if (levelIndex !== -1 && idx >= levelIndex) {
            delete updated[hierLevel];
          }
        });
      } else {
        delete updated[level];
      }
      return updated;
    });

    setSalesOptionsCache(prev => {
      const updated = { ...prev };
      if (hierarchyOrder.length > 0) {
        const levelIndex = hierarchyOrder.indexOf(level);
        hierarchyOrder.forEach((hierLevel, idx) => {
          if (levelIndex !== -1 && idx >= levelIndex) {
            delete updated[hierLevel];
          }
        });
      } else {
        delete updated[level];
      }
      return updated;
    });

    if (distributorSource === "sales" && selectedReport.distributorFilter?.enabled) {
      clearDistributorSelections();
    } else if (selectedReport.distributorFilter?.enabled && selectedReport.distributorFilter?.field) {
      const distributorFieldKey = selectedReport.distributorFilter.field;
      setFilters(prev => {
        const newFilters = { ...prev };
        if (newFilters[distributorFieldKey]) {
          delete newFilters[distributorFieldKey];
        }
        customFilters.forEach(filter => {
          delete newFilters[filter.alias];
        });
        return newFilters;
      });
      setOptionsMap(prev => {
        const updated = { ...prev };
        if (updated[distributorFieldKey]) {
          delete updated[distributorFieldKey];
        }
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
      setSearchTextMap(prev => {
        const updated = { ...prev };
        delete updated[distributorFieldKey];
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
    }

    loadSalesLevels();
  };

  const handleGeographicalLevelChange = (value: string | null) => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const prevLevel = filters[levelKey]?.[0];
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (value) {
        newFilters[levelKey] = [value];
        const valueIndex = geoHierarchyOrder.indexOf(value);
        let isNavigatingUp = false;
        if (valueIndex !== -1) {
          if (prevLevel) {
            const prevLevelIndex = geoHierarchyOrder.indexOf(prevLevel);
            if (prevLevelIndex !== -1 && prevLevelIndex > valueIndex) {
              isNavigatingUp = true;
            }
          }
          
          if (!isNavigatingUp) {
            for (let i = valueIndex + 1; i < geoHierarchyOrder.length; i++) {
              const childLevel = geoHierarchyOrder[i];
              if (prev[childLevel] && prev[childLevel].length > 0) {
                isNavigatingUp = true;
                break;
              }
            }
          }
        }
        
        let hasValidParentContext = valueIndex === 0 || isNavigatingUp;
        if (!hasValidParentContext && valueIndex > 0) {
          if (geoDrillDownPath.length > 0) {
            hasValidParentContext = geoDrillDownPath.some(item => {
              const itemIndex = geoHierarchyOrder.indexOf(item.level);
              return itemIndex !== -1 && itemIndex < valueIndex;
            });
          }
          
          if (!hasValidParentContext) {
            for (let i = 0; i < valueIndex; i++) {
              const parentLevel = geoHierarchyOrder[i];
              if (prev[parentLevel] && prev[parentLevel].length > 0) {
                hasValidParentContext = true;
                break;
              }
            }
          }
        }
        const committedValues = getValuesFromPath(geoDrillDownPath, value);
        const currentValues = prev[value] || [];
        if (currentValues.length > 0) {
          newFilters[value] = [...currentValues];
        } else if (committedValues.length > 0) {
          newFilters[value] = [...committedValues];
        } else if (!hasValidParentContext) {
          if (newFilters[value]) {
            delete newFilters[value];
          }
        }
      } else {
        delete newFilters[levelKey];
      }
      return newFilters;
    });
    
    const hasCache = value ? (geoOptionsCache[value] && geoOptionsCache[value].length > 0) : false;
    const hasOptionsInMap = value ? (optionsMap[value] && optionsMap[value].length > 0) : false;
    const shouldLoad = value && (!hasOptionsInMap || (prevLevel !== value && !hasCache));
    
    if (shouldLoad) {
      loadGeographicalValues(value);
    } else if (value && hasCache && !hasOptionsInMap) {
      setOptionsMap(prev => ({ ...prev, [value]: geoOptionsCache[value] }));
    }
    
    if (selectedReport.distributorFilter?.enabled && selectedReport.distributorFilter?.field) {
      setSearchTextMap(prev => {
        const updated = { ...prev };
        delete updated[selectedReport.distributorFilter!.field];
        return updated;
      });
    }
  };

  const handleGeographicalValueChange = (values: string[]) => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (!currentLevel) return;
    const currentLevelIndex = geoHierarchyOrder.indexOf(currentLevel);
    
    if (currentLevelIndex !== -1) {
      setOptionsMap(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        if (selectedReport.distributorFilter?.enabled) {
          delete updated[selectedReport.distributorFilter.field];
        }
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });

      setGeoOptionsCache(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        return updated;
      });
      
      setSearchTextMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
    }
    
    setFilters(prev => ({
      ...prev,
      [currentLevel]: values
    }));
    
  };

  const handleGeoNextLevel = () => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    
    
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (!currentLevel) return;
    
    setIsHierarchySyncing(true);
    geoApplyJustClicked.current = true;
    
    const updatedPath = buildHierarchyPathUpToLevel(
      geoDrillDownPath,
      filters,
      geoHierarchyOrder,
      currentLevel
    );
    setGeoDrillDownPath(updatedPath);
    
    const currentLevelIndex = geoHierarchyOrder.indexOf(currentLevel);
    
    if (currentLevelIndex !== -1) {
      setOptionsMap(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        if (selectedReport.distributorFilter?.enabled) {
          delete updated[selectedReport.distributorFilter.field];
        }
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
      
      clearMergedFilters();

      setGeoOptionsCache(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        return updated;
      });
      
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete newFilters[geoHierarchyOrder[i]];
        }
        
        customFilters.forEach(filter => {
          delete newFilters[filter.alias];
        });
        
        Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
          delete newFilters[mergedFilterAlias];
          const dynamicKey = `${mergedFilterAlias}_dynamic`;
          delete newFilters[dynamicKey];
        });
        
        return newFilters;
      });
      
      setSearchTextMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        return newFilters;
      });
    }
    
    loadGeographicalLevels();
  };

  const handleGeoPathReset = (level: string, startIndex: number) => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    
    const levelsToClear = new Set<string>();
    levelsToClear.add(level);
    
    setGeoDrillDownPath(prev => {
      const levelsAfterReset = prev.slice(startIndex).map(item => item.level);
      levelsAfterReset.forEach(l => levelsToClear.add(l));
      const newPath = prev.slice(0, startIndex);
      return newPath;
    });
    const levelIndex = geoHierarchyOrder.indexOf(level);
    if (levelIndex !== -1) {
      for (let i = levelIndex; i < geoHierarchyOrder.length; i++) {
        levelsToClear.add(geoHierarchyOrder[i]);
      }
    }
    setFilters(prev => {
      const newFilters = { ...prev };
      
      delete newFilters[levelKey];
      
      levelsToClear.forEach(levelToClear => {
        delete newFilters[levelToClear];
      });
      
      customFilters.forEach(filter => {
        delete newFilters[filter.alias];
      });
      
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete newFilters[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete newFilters[dynamicKey];
      });
      
      return newFilters;
    });
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      
      levelsToClear.forEach(levelToClear => {
        delete updated[levelToClear];
      });
      
      customFilters.forEach(filter => {
        delete updated[filter.alias];
      });
      
      return updated;
    });

    setGeoOptionsCache(prev => {
      const updated = { ...prev };
      
      levelsToClear.forEach(levelToClear => {
        delete updated[levelToClear];
      });
      
      return updated;
    });

    if (distributorSource === "geographical" && selectedReport.distributorFilter?.enabled) {
      clearDistributorSelections();
    } else if (selectedReport.distributorFilter?.enabled && selectedReport.distributorFilter?.field) {
      setSearchTextMap(prev => {
        const updated = { ...prev };
        delete updated[selectedReport.distributorFilter!.field];
        return updated;
      });
    }

    loadGeographicalLevels();
  };

  const handleSalesDropdownClose = useCallback(() => {
    if (!selectedReport?.salesHierarchyFilter) return;
    
    if (salesApplyJustClicked.current) {
      salesApplyJustClicked.current = false;
      setIsHierarchySyncing(false);
      return;
    }

    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    
    setFilters(prev => {
      const newFilters = { ...prev };
      let hasChanges = false;
      
      hierarchyOrder.forEach(level => {
        const filterValues = prev[level] || [];
        const committedValues = getValuesFromPath(salesDrillDownPath, level);
        
        if (filterValues.length > 0 && committedValues.length === 0) {
          delete newFilters[level];
          hasChanges = true;
        }
        else if (filterValues.length > 0 && JSON.stringify(filterValues.sort()) !== JSON.stringify(committedValues.sort())) {
          if (committedValues.length > 0) {
            newFilters[level] = [...committedValues];
          } else {
            delete newFilters[level];
          }
          hasChanges = true;
        }
      });
      
      if (salesDrillDownPath.length === 0 && newFilters[levelKey]) {
        delete newFilters[levelKey];
        hasChanges = true;
      }
      
      return hasChanges ? newFilters : prev;
    });
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      
      hierarchyOrder.forEach(level => {
        const committedValues = getValuesFromPath(salesDrillDownPath, level);
        if (committedValues.length === 0 && updated[level]) {
          delete updated[level];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
    
    setSalesOptionsCache(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      
      hierarchyOrder.forEach(level => {
        const committedValues = getValuesFromPath(salesDrillDownPath, level);
        if (committedValues.length === 0 && updated[level]) {
          delete updated[level];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
    
    setIsHierarchySyncing(false);
  }, [selectedReport?.salesHierarchyFilter, salesDrillDownPath]);

  const handleGeoDropdownClose = useCallback(() => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    
    if (geoApplyJustClicked.current) {
      geoApplyJustClicked.current = false;
      setIsHierarchySyncing(false);
      return;
    }
    
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    
    setFilters(prev => {
      const newFilters = { ...prev };
      let hasChanges = false;
      
      geoHierarchyOrder.forEach(level => {
        const filterValues = prev[level] || [];
        const committedValues = getValuesFromPath(geoDrillDownPath, level);
        
        if (JSON.stringify(filterValues.sort()) !== JSON.stringify(committedValues.sort())) {
          if (committedValues.length > 0) {
            newFilters[level] = [...committedValues];
          } else {
            delete newFilters[level];
          }
          hasChanges = true;
        }
      });
      
      if (geoDrillDownPath.length === 0 && newFilters[levelKey]) {
        delete newFilters[levelKey];
        hasChanges = true;
      }
      
      return hasChanges ? newFilters : prev;
    });
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      
      geoHierarchyOrder.forEach(level => {
        const committedValues = getValuesFromPath(geoDrillDownPath, level);
        if (committedValues.length === 0 && updated[level]) {
          delete updated[level];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
    
    setGeoOptionsCache(prev => {
      const updated = { ...prev };
      let hasChanges = true;
      
      geoHierarchyOrder.forEach(level => {
        const committedValues = getValuesFromPath(geoDrillDownPath, level);
        if (committedValues.length === 0 && updated[level]) {
          delete updated[level];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
    
    setIsHierarchySyncing(false);
  }, [selectedReport?.geographicalHierarchyFilter, geoDrillDownPath, geoHierarchyOrder]);

  const handleReset = () => {
    const today = dayjs();
    setFromDate(today);
    setToDate(today);
    setFilters({});
    setOptionsMap({
      distributor_division: distributorMetaOptionCache.divisions,
      distributor_type: distributorMetaOptionCache.types
    });
    setSalesOptionsCache({});
    setGeoOptionsCache({});
    setSalesDrillDownPath([]);
    setGeoDrillDownPath([]);
    setShowPreview(false);
    setDateFilterKey(prev => prev + 1); // Reset date filter component
    
    if (customFilters.length > 0) {
      setSearchTextMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
    }
  };

  const handleSalesResetAll = () => {
    if (!selectedReport?.salesHierarchyFilter) return;
    
    const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[levelKey];
      hierarchyOrder.forEach(level => {
        delete newFilters[level];
      });
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete newFilters[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete newFilters[dynamicKey];
      });
      return newFilters;
    });
    
    setSalesDrillDownPath([]);
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      hierarchyOrder.forEach(level => {
        delete updated[level];
      });
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete updated[mergedFilterAlias];
        const sources = selectedReport.mergedFilters?.[mergedFilterAlias] || [];
        sources.forEach(source => {
          delete updated[source.alias];
        });
      });
      return updated;
    });
    setSalesOptionsCache({});

    if (distributorSource === "sales" && selectedReport.distributorFilter?.enabled) {
      clearDistributorSelections();
    }
  };

  const handleGeoResetAll = () => {
    if (!selectedReport?.geographicalHierarchyFilter) return;
    
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[levelKey];
      geoHierarchyOrder.forEach(level => {
        delete newFilters[level];
      });
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete newFilters[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete newFilters[dynamicKey];
      });
      return newFilters;
    });
    
    setGeoDrillDownPath([]);
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      geoHierarchyOrder.forEach(level => {
        delete updated[level];
      });
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete updated[mergedFilterAlias];
        const sources = selectedReport.mergedFilters?.[mergedFilterAlias] || [];
        sources.forEach(source => {
          delete updated[source.alias];
        });
      });
      return updated;
    });
    setGeoOptionsCache({});

    if (distributorSource === "geographical" && selectedReport.distributorFilter?.enabled) {
      clearDistributorSelections();
    }
  };

  const handleCreateReport = async () => {
    if (!selectedReport) {
      return;
    }

    setShowPreview(true);
  };

  const getDistributorCodes = useCallback((): string[] => {
    if (!selectedReport) return [];
    
    const distributorFilterField = selectedReport.distributorFilter?.field || 'distributor_code';
    const hierarchyOrder = selectedReport.salesHierarchyFilter?.hierarchyOrder || [];
    
    if (filters[distributorFilterField] && filters[distributorFilterField].length > 0) {
      return filters[distributorFilterField];
    }
    
    const authContext = JSON.parse(localStorage.authContext || '{}');
    const loggedInUser = authContext?.user?.loginId;
    if (selectedReport.isDistributorView && loggedInUser) {
      return [loggedInUser];
    }
    
    return [];
  }, [selectedReport, filters]);



useEffect(() => {
  if (customFilters.length > 0) {
    setFilters(prev => {
      const newFilters = { ...prev };
      customFilters.forEach(filter => {
        if (newFilters[filter.alias]) {
          delete newFilters[filter.alias];
        }
      });
      Object.keys(selectedReport?.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete newFilters[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete newFilters[dynamicKey];
      });
      return newFilters;
    });
    
    setCustomFilterSelectionOrder([]);
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      customFilters.forEach(filter => {
        delete updated[filter.alias];
      });
      Object.keys(selectedReport?.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete updated[mergedFilterAlias];
        const sources = selectedReport?.mergedFilters?.[mergedFilterAlias] || [];
        sources.forEach(source => {
          delete updated[source.alias];
        });
      });
      return updated;
    });
    
    setSearchTextMap(prev => {
      const updated = { ...prev };
      customFilters.forEach(filter => {
        delete updated[filter.alias];
      });
      Object.keys(selectedReport?.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete updated[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete updated[dynamicKey];
      });
      return updated;
    });
  }
}, [fromDate, toDate, customFilters, selectedReport]);

  
  const isMergedFilter = useCallback((filterAlias: string): boolean => {
    return isMergedFilterForReport(filterAlias, selectedReport);
  }, [selectedReport]);

  const clearMergedFilters = useCallback(() => {
    if (!selectedReport?.mergedFilters) return;
    
    setFilters(prev => {
      const newFilters = { ...prev };
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete newFilters[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete newFilters[dynamicKey];
      });
      return newFilters;
    });
    
    setSearchTextMap(prev => {
      const updated = { ...prev };
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete updated[mergedFilterAlias];
        const dynamicKey = `${mergedFilterAlias}_dynamic`;
        delete updated[dynamicKey];
      });
      return updated;
    });
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      Object.keys(selectedReport.mergedFilters || {}).forEach(mergedFilterAlias => {
        delete updated[mergedFilterAlias];
        const sources = selectedReport.mergedFilters?.[mergedFilterAlias] || [];
        sources.forEach(source => {
          delete updated[source.alias];
        });
      });
      return updated;
    });
  }, [selectedReport]);

  const buildMergedFilterDependencies = useCallback((excludeKey?: string, overrideFilters?: Record<string, string[]>): Record<string, string[]> => {
    const dependencyFilters: Record<string, string[]> = {};
    const sourceFilters = overrideFilters || filters;

    const allMergedFilterSourceAliases = new Set<string>();
    if (selectedReport?.mergedFilters) {
      Object.entries(selectedReport.mergedFilters).forEach(([mergedFilterAlias, sources]) => {
        sources.forEach(source => {
          allMergedFilterSourceAliases.add(source.alias);
        });
      });
    }

    const isExcludingMergedFilterSource = excludeKey && allMergedFilterSourceAliases.has(excludeKey);

    if (!isExcludingMergedFilterSource && selectedReport?.mergedFilters) {
      Object.entries(selectedReport.mergedFilters).forEach(([mergedFilterAlias, sources]) => {
        if (mergedFilterAlias === excludeKey) return;
        
        const mergedFilterSelections = sourceFilters[mergedFilterAlias] || [];
        if (mergedFilterSelections.length > 0) {
          const selectedValue = mergedFilterSelections[0]; // Single-select, so get first value
          const sourceAlias = getSourceAliasFromMergedFilter(mergedFilterAlias, selectedValue);
          
          if (sourceAlias) {
            if (excludeKey === sourceAlias) {
              return; // Don't include this source alias in dependencies when loading its options
            }
            const dynamicKey = `${mergedFilterAlias}_dynamic`;
            const dynamicSelections = sourceFilters[dynamicKey] || [];
            
            if (dynamicSelections.length > 0) {
              dependencyFilters[sourceAlias] = dynamicSelections;
            } else {
            }
          }
        }
      });
    }

    customFilters.forEach(cf => {
      if (cf.alias === excludeKey) return;
      const vals = sourceFilters[cf.alias];
      if (vals && vals.length > 0 && vals[0] !== "") {
        dependencyFilters[cf.alias] = vals;
      }
    });

    return dependencyFilters;
  }, [filters, customFilters, selectedReport?.mergedFilters, getSourceAliasFromMergedFilter]);

  const loadMergedFilterOptions = useCallback((mergedFilterAlias: string, q?: string) => {
    const mergedSources = getMergedFilterSources(mergedFilterAlias, selectedReport);
    if (!mergedSources || mergedSources.length === 0) {
      return;
    }

    const allOptions: { label: string; value: string }[] = [];

    mergedSources.forEach(source => {
      const value = source.value;
      const label = source.value; // Use value as label
      
      if (!q || label.toLowerCase().includes(q.toLowerCase())) {
        allOptions.push({ label, value });
      }
    });

    setOptionsMap(prev => ({ ...prev, [mergedFilterAlias]: allOptions }));
  }, [selectedReport]);


  const loadFilterOptions = useCallback(async (key: string, q?: string, overrideFilters?: Record<string, string[]>) => {
    if (!selectedReport?.reportName) {
      return;
    }
    
    const effectiveFilters = overrideFilters || filters;
    setLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      const locationFilters = buildLocationFilters(geoDrillDownPath);
      const userFilters = buildUserFilters(
        salesDrillDownPath,
        selectedReport.salesHierarchyFilter?.hierarchyOrder || []
      );
      const distributorCodes = getDistributorCodes();
      const additionalFilters: Record<string, string[]> | undefined = 
        distributorCodes.length > 0 
          ? { distributor_code: distributorCodes }
          : undefined;

      const dependencyFilters = buildMergedFilterDependencies(key, effectiveFilters);
      let since: string | undefined;
      let until: string | undefined;
      
      if (fromDate && toDate) {
        const fromDateDayjs = dayjs(fromDate);
        const toDateDayjs = dayjs(toDate);
        
        since = new Date(Date.UTC(
          fromDateDayjs.year(),
          fromDateDayjs.month(),
          fromDateDayjs.date() - 1,
          18,
          30,
          0
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
        
        until = new Date(Date.UTC(
          toDateDayjs.year(),
          toDateDayjs.month(),
          toDateDayjs.date(),
          18,
          29,
          59
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
      }

      if(selectedReport?.dateRangeFilter === false){
        since = undefined;
        until = undefined;
      }

      const distributorFilterPayload =
        (locationFilters.length > 0 || userFilters.length > 0)
          ? {
              locationFilters: locationFilters.length > 0 ? locationFilters : undefined,
              userFilters: userFilters.length > 0 ? userFilters : undefined
            }
          : undefined;

      const values = await fetchFilterValues({
        report: selectedReport.reportName,
        which: key,
        contains: q?.trim(),
        page: 1,
        pageSize: 50,
        filters: Object.keys(dependencyFilters).length > 0 ? dependencyFilters : undefined,
        additionalFilters,
        since,
        until,
        distributorFilter: distributorFilterPayload

      });
      const filteredValues = values.filter(v => v && String(v).trim() !== '');
      const opts = filteredValues.map(v => ({ label: String(v), value: String(v) }));
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
    } catch (error) {
      console.error('[loadFilterOptions] Error loading options for:', key, error);
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [
    selectedReport?.reportName, 
    getDistributorCodes, 
    fromDate, 
    toDate, 
    buildMergedFilterDependencies,
    geoDrillDownPath,
    salesDrillDownPath,
    customFilters,
    filters
  ]);

  const handleMergedFilterChange = useCallback((mergedFilterAlias: string, values: string[]) => {
    const dynamicKey = `${mergedFilterAlias}_dynamic`;
    const previousMergedSelection = filters[mergedFilterAlias]?.[0] || null;
    const newMergedSelection = values.length > 0 ? values[0] : null;
    
    const previousSourceAlias = previousMergedSelection 
      ? getSourceAliasFromMergedFilter(mergedFilterAlias, previousMergedSelection) 
      : null;
    const newSourceAlias = newMergedSelection 
      ? getSourceAliasFromMergedFilter(mergedFilterAlias, newMergedSelection) 
      : null;
    
    const shouldClearDynamic = !newMergedSelection || previousMergedSelection !== newMergedSelection;
    const hadDynamicSelections = filters[dynamicKey] && filters[dynamicKey].length > 0 && filters[dynamicKey][0] !== "";

    // Build next filters snapshot (synchronous)
    const nextFiltersSnapshot: Record<string, string[]> = {
      ...filters,
      [mergedFilterAlias]: values
    };
    if (shouldClearDynamic) {
      delete nextFiltersSnapshot[dynamicKey];
    }
    if (hadDynamicSelections) {
      customFilters.forEach(filter => {
        delete nextFiltersSnapshot[filter.alias];
      });
    }

    // Apply filters state once
    setFilters(nextFiltersSnapshot);

    // Update search text map
    setSearchTextMap(prevSearch => {
      const updated = { ...prevSearch };
      delete updated[dynamicKey];
      if (hadDynamicSelections) {
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
      }
      return updated;
    });

    // Update options map and selection order
    setOptionsMap(prevOptions => {
      const updated = { ...prevOptions };
      if (shouldClearDynamic) {
        if (hadDynamicSelections) {
          customFilters.forEach(filter => {
            delete updated[filter.alias];
          });
          if (previousSourceAlias && previousSourceAlias !== newSourceAlias) {
            delete updated[previousSourceAlias];
          }
        } else if (previousSourceAlias && previousSourceAlias !== newSourceAlias) {
          delete updated[previousSourceAlias];
        }
      }
      return updated;
    });
    if (shouldClearDynamic && hadDynamicSelections) {
      setCustomFilterSelectionOrder([]);
    }

    // Load options for new source using snapshot
    if (values.length > 0 && values[0]) {
      const selectedValue = values[0];
      const selectedSourceAlias = getSourceAliasFromMergedFilter(mergedFilterAlias, selectedValue);
      if (selectedSourceAlias && loadFilterOptions) {
        loadFilterOptions(selectedSourceAlias, undefined, nextFiltersSnapshot);
      }
    }

    // Invalidate cached options/search for other custom filters that have no selections
    const filtersToInvalidate = customFilters
      .map(cf => cf.alias)
      .filter(alias => alias !== mergedFilterAlias)
      .filter(alias => {
        const vals = nextFiltersSnapshot[alias];
        return !vals || vals.length === 0 || vals[0] === "";
      });

    if (filtersToInvalidate.length > 0) {
      setOptionsMap(prev => {
        const updated = { ...prev };
        filtersToInvalidate.forEach(alias => {
          delete updated[alias];
        });
        return updated;
      });

      setSearchTextMap(prev => {
        const updated = { ...prev };
        filtersToInvalidate.forEach(alias => {
          delete updated[alias];
        });
        return updated;
      });
    }
  }, [filters, customFilters, getSourceAliasFromMergedFilter, loadFilterOptions]);

  const loadDistributorMetaOptions = useCallback(async () => {
    setLoadingMap(prev => ({
      ...prev,
      distributor_type: true,
      distributor_division: true
    }));
    try {
      const meta = await fetchDistributorMeta();
      const divisionOptions = meta.divisions.map(value => ({ label: value, value }));
      const typeOptions = meta.types.map(value => ({ label: value, value }));
      setDistributorFeatures(meta.features);
      setDistributorMetaOptionCache({
        types: typeOptions,
        divisions: divisionOptions
      });

      setOptionsMap(prev => ({
        ...prev,
        distributor_division: divisionOptions,
        distributor_type: typeOptions
      }));
    } catch (error) {
      setOptionsMap(prev => ({
        ...prev,
        distributor_division: prev.distributor_division || distributorMetaOptionCache.divisions,
        distributor_type: prev.distributor_type || distributorMetaOptionCache.types
      }));
    } finally {
      setLoadingMap(prev => ({
        ...prev,
        distributor_type: false,
        distributor_division: false
      }));
    }
  }, []);

  const loadDistributorOptions = useCallback(async (fieldKey: string) => {
    if (!selectedReport) return;
    
    setLoadingMap(prev => ({ ...prev, [fieldKey]: true }));
    try {
      let filterIds: string[] = [];
      let attemptedTargetedFetch = false;

      if (distributorSource === "geographical" && selectedReport.geographicalHierarchyFilter?.enabled) {
        if (geoDrillDownPath.length > 0) {
          const level = geoDrillDownPath[geoDrillDownPath.length - 1].level;
          const values = geoDrillDownPath
            .filter(item => item.level === level)
            .map(item => item.value);
          if (values.length > 0) {
            attemptedTargetedFetch = true;
            const supplierPromises = values.map(value =>
              fetchLocationUsers(level, value, "supplier")
            );
            const supplierResults = await Promise.all(supplierPromises);
            filterIds = Array.from(new Set(supplierResults.flat()));
          }
        }
      }

      if (distributorSource === "sales" && selectedReport.salesHierarchyFilter?.enabled) {
        const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
        if (hierarchyOrder.length > 0 && salesDrillDownPath.length > 0) {
          const supplierLevel = hierarchyOrder[hierarchyOrder.length - 1];

          const supplierSelections = salesDrillDownPath
            .filter(item => item.level === supplierLevel)
            .map(item => item.value);

          if (supplierSelections.length > 0) {
            attemptedTargetedFetch = true;
            filterIds = supplierSelections;
          } else {
            const targetParent = [...salesDrillDownPath]
              .reverse()
              .find(item => {
                const levelIndex = hierarchyOrder.indexOf(item.level);
                const supplierIndex = hierarchyOrder.indexOf(supplierLevel);
                return levelIndex !== -1 && supplierIndex !== -1 && levelIndex < supplierIndex;
              });

            if (targetParent) {
              attemptedTargetedFetch = true;
              const parentSelections = salesDrillDownPath
                .filter(item => item.level === targetParent.level)
                .map(item => item.value);

              const childrenPromises = parentSelections.map(userId =>
                fetchChildrenUsers(userId, supplierLevel)
              );
              const childrenResults = await Promise.all(childrenPromises);
              filterIds = Array.from(new Set(childrenResults.flat()));
            }
          }
        }
      }
      
      if (filterIds.length === 0 && !attemptedTargetedFetch) {
        const allSuppliers = await fetchUsersByDesignation('supplier');
        filterIds = allSuppliers;
      }

      const selectedTypes = filters['distributor_type'] || [];
      const selectedDivisions = filters['distributor_division'] || [];

      let filteredLoginIds: string[] = [];
      
      if ((selectedTypes.length > 0 || selectedDivisions.length > 0) && distributorFeatures.length > 0) {
        filteredLoginIds = filterDistributorsBySelections(
          distributorFeatures,
          selectedTypes,
          selectedDivisions,
          filterIds.length > 0 ? filterIds : undefined
        );
      } else {
        filteredLoginIds = filterIds;
      }
      
      const opts = filteredLoginIds.map(u => ({ label: u, value: u }));
      setOptionsMap(prev => ({ ...prev, [fieldKey]: opts }));
    } catch (error) {
      setOptionsMap(prev => ({ ...prev, [fieldKey]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [fieldKey]: false }));
    }
  }, [selectedReport, filters, geoDrillDownPath, salesDrillDownPath, distributorSource, distributorFeatures]);

  useEffect(() => {
    const loadCustomFiltersData = async () => {
      setCustomFiltersLoading(true);
      try {
        const filters = await loadCustomFiltersForReport(selectedReport);
        setCustomFilters(filters);
      } finally {
        setCustomFiltersLoading(false);
      }
    };
    
    loadCustomFiltersData();
  }, [selectedReport]);

  useEffect(() => {
    loadDistributorMetaOptions();
  }, [loadDistributorMetaOptions]);

  useEffect(() => {
    if (currentReport) {
      const today = dayjs(); 
      setFromDate(today);
      setToDate(today);
      setFilters({});
      setOptionsMap({
        distributor_division: distributorMetaOptionCache.divisions,
        distributor_type: distributorMetaOptionCache.types
      });
      setSalesOptionsCache({});
      setGeoOptionsCache({});
      setSalesDrillDownPath([]);
      setGeoDrillDownPath([]);
      setShowPreview(false);
      setSearchTextMap({});
      setDistributorSource(resolveDefaultDistributorSource(currentReport.newReportConfig ?? null));
      setDateFilterKey(prev => prev + 1); 
    }
  }, [currentReport?.id]);

  const geoDrillDownPathKey = useMemo(() => JSON.stringify(geoDrillDownPath), [geoDrillDownPath]);
  const salesDrillDownPathKey = useMemo(() => JSON.stringify(salesDrillDownPath), [salesDrillDownPath]);
  
  const distributorTypeKey = useMemo(() => JSON.stringify(filters['distributor_type'] || []), [filters['distributor_type']]);
  const distributorDivisionKey = useMemo(() => JSON.stringify(filters['distributor_division'] || []), [filters['distributor_division']]);
  
  useEffect(() => {
    if (selectedReport?.distributorFilter?.enabled) {
      const fieldKey = selectedReport.distributorFilter.field;
      setOptionsMap(prev => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });

      setFilters(prev => {
        const updated = { ...prev };
        if (updated[fieldKey]) {
          delete updated[fieldKey];
        }
        return updated;
      });
      setSearchTextMap(prev => {
        const updated = { ...prev };
        if (updated[fieldKey]) {
          delete updated[fieldKey];
        }
        return updated;
      });
    }
    
  }, [selectedReport?.distributorFilter?.enabled, geoDrillDownPathKey, salesDrillDownPathKey, distributorTypeKey, distributorDivisionKey, distributorSource]);

  useEffect(() => {
    return () => {
      Object.entries(timeoutRefs.current).forEach(([key, timeout]) => {
        if (timeout) {
          clearTimeout(timeout);
          delete timeoutRefs.current[key]; // Clean up ref entries
        }
      });
    };
  }, []);

  const getReportById = (id: string) => {
    for (let g of groupedReports) {
      const found = g.children.find((r) => r.id === id);
      if (found) return found;
    }
    return null;
  };

  const fetchUiconfig = useCallback(async () => {
    const currentUserData = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user;

    const allConfig = await getAllConfiguartions();
    const clientConfig = allConfig.data.features.filter((c: any) => c.domainName === "clientconfig");
    const uiConfig = allConfig.data.features.filter((c: any) => c.domainName === "uiconfig");

    const uiConfigKey = clientConfig.find((c: any) => c.domainType === "config")
      ?.domainValues?.[0]?.["uiConfigKey"];

    const roleBasedAccess = isRoleConfigEnabled(clientConfig);
    const isAdmin = currentUserData?.designation?.includes("admin");

    let finalList: ReportItem[] = [];

    if (isAdmin) {
      const confName = "admin_report_configuration";
      const adminConfig = clientConfig.find((c: any) => c.domainType === confName);
      finalList = adminConfig?.domainValues || getReportConfig("admin", uiConfigKey, uiConfig);
    } else {
      let merged: ReportItem[] = [];
      const roles = getUserRolesBasedOnAccess(roleBasedAccess, currentUserData);

      for (let role of roles) {
        const confName = `${role}_report_configuration`;
        const rc = clientConfig.find((c: any) => c.domainType === confName);
        if (rc?.domainValues) merged = [...merged, ...rc.domainValues];
      }
      
      finalList = Array.from(new Set(merged.map((i) => i.id))).map(
        (id) => merged.find((i) => i.id === id)!
      );

      if (finalList.length === 0) {
        for (let role of roles) {
          merged = [...merged, ...getReportConfig(role, uiConfigKey, uiConfig)];
        }
        finalList = Array.from(new Set(merged.map((i) => i.id))).map(
          (id) => merged.find((i) => i.id === id)!
        );
      }
    }
    const filteredList = finalList.filter(r=> r.isNewReportUi)
    const grouped: { [k: string]: ReportGroup } = {};
    filteredList.forEach((r) => {
      if (!grouped[r.type]) {
        grouped[r.type] = {
          type: r.type,
          name: `${r.type} Report`,
          children: [],
        };
      }
      grouped[r.type].children.push(r);
    });

    setGroupedReports(Object.values(grouped));
  }, []);

  useEffect(() => {
    fetchUiconfig();
  }, []);

  const toggleType = (t: string) => setOpenType((prev) => (prev === t ? null : t));

  const getFilterChips = () => {
    const chips: Array<{ 
      key: string; 
      label: string; 
      value: string; 
      count?: number;
      type: 'filter' | 'geo' | 'sales' | 'distributor';
      level?: string;
      startIndex?: number;
    }> = [];

    if (filters['distributor_type']?.length > 0) {
      const values = filters['distributor_type'];
      chips.push({
        key: 'distributor_type',
        label: 'Distributor type',
        value: values[0],
        count: values.length > 1 ? values.length - 1 : undefined,
        type: 'filter'
      });
    }

    if (filters['distributor_division']?.length > 0) {
      const values = filters['distributor_division'];
      chips.push({
        key: 'distributor_division',
        label: 'Distributor division',
        value: values[0],
        count: values.length > 1 ? values.length - 1 : undefined,
        type: 'filter'
      });
    }

    if (selectedReport?.distributorFilter?.enabled && distributorFieldKey) {
      const distributorValues = filters[distributorFieldKey] || [];
      if (distributorValues.length > 0) {
        const distributorOptions = optionsMap[distributorFieldKey] || [];
        const firstValueLabel = distributorOptions.find(opt => opt.value === distributorValues[0])?.label || distributorValues[0];
        
        chips.push({
          key: distributorFieldKey,
          label: 'Distributor',
          value: firstValueLabel,
          count: distributorValues.length > 1 ? distributorValues.length - 1 : undefined,
          type: 'distributor'
        });
      }
    }

    const pushHierarchyChip = (
      summaryItem: PathSummaryItem,
      type: 'geo' | 'sales'
    ) => {
      const extraCount = summaryItem.count > 1 ? summaryItem.count - 1 : 0;
      chips.push({
        key: `${type}_${summaryItem.level}_${summaryItem.startIndex}`,
        label: summaryItem.level,
        value: summaryItem.firstValue || "",
        count: extraCount > 0 ? extraCount : undefined,
        type,
        level: summaryItem.level,
        startIndex: summaryItem.startIndex
      });
    };

    geoPathSummary.forEach(item => pushHierarchyChip(item, 'geo'));
    salesPathSummary.forEach(item => pushHierarchyChip(item, 'sales'));

    return chips;
  };

  const handleChipDelete = (chip: any) => {
    if (chip.type === 'filter') {
      const shouldClearDistributor = 
        (chip.key === 'distributor_type' || chip.key === 'distributor_division') && 
        selectedReport?.distributorFilter?.enabled;
      
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[chip.key];
        
        if (shouldClearDistributor && selectedReport?.distributorFilter?.field) {
          const distributorFieldKey = selectedReport.distributorFilter.field;
          if (newFilters[distributorFieldKey]) {
            delete newFilters[distributorFieldKey];
          }
        }
        
        return newFilters;
      });
      
      if (shouldClearDistributor && selectedReport?.distributorFilter?.field) {
        const distributorFieldKey = selectedReport.distributorFilter.field;
        setOptionsMap(prev => {
          if (!prev[distributorFieldKey]) {
            return prev;
          }
          const updated = { ...prev };
          delete updated[distributorFieldKey];
          return updated;
        });
        setSearchTextMap(prev => {
          if (!prev[distributorFieldKey]) {
            return prev;
          }
          const updated = { ...prev };
          delete updated[distributorFieldKey];
          return updated;
        });
      }
    } else if (chip.type === 'distributor') {
      clearDistributorSelections();
    } else if (chip.type === 'geo') {
      handleGeoPathReset(chip.level!, chip.startIndex!);
    } else if (chip.type === 'sales') {
      handleSalesPathReset(chip.level!, chip.startIndex!);
    }
  };

  const filterChips = getFilterChips();

  const memoizedDateRange = useMemo(() => ({
    type: "daterange",
    fromDate: fromDate?.format("DD-MM-YYYY") ?? dayjs().format("DD-MM-YYYY"),
    toDate: toDate?.format("DD-MM-YYYY") ?? dayjs().format("DD-MM-YYYY"),
    selectedRange: "custom"
  }), [fromDate, toDate]);

  const hasSalesDrillDown = salesDrillDownPath.length > 0;
  const hasGeoDrillDown = geoDrillDownPath.length > 0;
  const hasDirectDistributor = distributorFieldKey && filters[distributorFieldKey]?.length > 0;
  const isDownloadDisabled = !hasSalesDrillDown && !hasGeoDrillDown && !hasDirectDistributor && !isDistributorView;
  const showTopFilterBar = !isDistributorView;
  const isCreateReportDisabled = !hasSalesDrillDown && !hasGeoDrillDown && !hasDirectDistributor && !isDistributorView;

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownload = async (format: string) => {
    if (!selectedReport?.reportName) {
      handleDownloadClose();
      return;
    }

    const fromDateDayjs = fromDate;
    const toDateDayjs = toDate;
    const dateRangeType = "daterange" as "daterange" | "period";

    if (dateRangeType === "daterange" && fromDateDayjs.isAfter(toDateDayjs)) {
      openPopup("Error", "To Date should be greater than from date.");
      handleDownloadClose();
      return;
    }
    handleDownloadClose();
    setDownloadLoader(true);

    try {
      const result = await handleMdmReportDownload({
        selectedReport: selectedReport,
        filters,
        dateRangeType,
        fromDate: fromDateDayjs,
        toDate: toDateDayjs,
        period: undefined,
        year: undefined,
        format,
        primaryFilter: null, 
        customFilters: customFilters.map(filter => filter.alias),
        salesDrillDownPath,
        geoDrillDownPath
      });

      setDownloadLoader(false);

      if (result.success) {
        openPopup("Success", result.message);
      } else {
        openPopup("Error", result.message);
      }

    } catch (error: any) {
      setDownloadLoader(false);
      const errorMessage = error.message || "Failed to download report. Please try again.";
      openPopup("Error", errorMessage);
    }
  };
  return (
    <div style={{ width: "100%", padding: "8px", display: "flex", flexDirection: "column", height: "100vh" }}>
      <ConfirmationPopUp
        openConfirmModal={showSourceChangeConfirm}
        message="Do you want to clear the selection and continue?"
        setOpenConfirmModal={(value) => {
          setShowSourceChangeConfirm(value);
          if (!value) {
            pendingSourceChange.current = null;
          }
        }}
        successMethod={handleConfirmSourceChange}
        rejectMethod={handleCancelSourceChange}
        rightButtonText="Continue"
      />
      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, position: 'relative', marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            <ArrowBack
              onClick={() => navigate(-1)}
              sx={{ fontSize: "18px", cursor: "pointer" }}
            />

            <Box
              sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
              onClick={() => setOpenDropdown((p) => !p)}
            >
              <Box sx={{ fontSize: "15px", fontWeight: 500 }}>{currentReport?.name}</Box>
              <FontAwesomeIcon
                icon={openDropdown ? faAngleUp : faAngleDown}
                style={{
                  fontSize: "6px",
                  color: "#888",             // arrow color
                  border: "1px solid #888", // circle border
                  borderRadius: "50%",         // make it round
                  padding: "3px",              // adjust space inside circle
                  cursor: "pointer",
                  width: "11px",
                  height: "11px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#F2F2F2",
                  justifyContent: "center"
                }}
              />
            </Box>
          </Box>

          {openDropdown && (
            <Box sx={{ width: "300px", position: 'absolute', left: 0, top: "36px", zIndex: 10 }}>
              <Box
                sx={{
                  background: "#F2F2F2",
                  padding: "10px 14px",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  color: "#888",
                  fontSize: "14px",
                  whiteSpace: 'normal',
                  wordBreak: "break-word",
                }}
              >
                Reports - {currentReport?.name} </Box>

              <Box
                sx={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  background: "#fff",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  padding: "12px",
                  whiteSpace: 'normal',
                  wordBreak: "break-word",
                }}
              >
                {groupedReports.map((group, index) => {
                  const isLast = index === groupedReports.length - 1;
                  const isOpen = openType === group.type;

                  const capitalize = (str: string) =>
                    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

                  return (
                    <Box key={group.type} sx={{ marginBottom: isLast ? "0px" : "10px" }}>
                      <Box
                        onClick={() => toggleType(group.type)}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "5px",
                          fontSize: "14px",
                          borderRadius: "6px",
                          background: isOpen ? "#e7f9f7" : "transparent",
                          color:
                            isOpen || (openDropdown && currentReport?.type === group.type)
                              ? "#00A392"
                              : "#888",
                          fontWeight: 500,
                          whiteSpace: 'normal',
                          wordBreak: "break-word",
                        }}
                      >
                        {capitalize(group.type) + " Report"}
                        <FontAwesomeIcon
                          icon={isOpen ? faAngleUp : faAngleRight}
                          style={{
                            fontSize: "8px",
                            backgroundColor:
                            isOpen || (openDropdown && currentReport?.type === group.type)
                              ? "#00A392"
                              : "#F2F2F2",   
                            color: isOpen || (openDropdown && currentReport?.type === group.type)
                              ? "white"
                              : "#888",         
                            borderRadius: "50%",        
                            padding: "3px",             
                            cursor: "pointer",
                            width: "12px",
                            height: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        />
                      </Box>

                      {isOpen && (
                        <Box sx={{ marginLeft: "14px", marginTop: "6px", whiteSpace: 'normal', wordBreak: "break-word" }}>
                          {group.children.map((child) => (
                            <Box
                              key={child.id}
                              onClick={() => {
                                const full = getReportById(child.id);
                                if (full) setCurrentReport(full);
                                setOpenDropdown(false);
                                setOpenType(null);
                              }}
                              sx={{
                                padding: "5px 14px",
                                fontSize: "14px",
                                borderRadius: "8px",
                                marginBottom: "4px",
                                background:
                                  child.id === currentReport?.id ? "#e6fbf8" : "transparent",
                                color: child.id === currentReport?.id ? "#169c87" : "#555",
                                cursor: "pointer",
                                position: "relative",
                                whiteSpace: 'normal',
                                wordBreak: "break-word",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: "-12px",
                                  width: "8px",
                                  height: "2px",
                                  background: "#ccc",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              />
                              {child.name}
                            </Box>
                          ))}
                        </Box>
                      )}

                      {!isLast && (
                        <Box
                          sx={{
                            borderBottom: "1px dotted #ccc",
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

            </Box>
          )}
        </div>
        {selectedReport?.dateRangeFilter === true && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Box component="span" sx={{ color: 'grey', fontSize: '14px', fontWeight: 500 }}>Date Range</Box>
            <Box component="span" sx={{ color: 'red', fontSize: '16px', fontWeight: 600, lineHeight: 1 }}>*</Box>
          </Box>
          <DateRangeSelector
            key={dateFilterKey}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={(date) => {
              if (date) {
                setFromDate(date);
              }
            }}
            setToDate={(date) => {
              if (date) {
                setToDate(date);
              }
            }}
            reportConfig={selectedReport}
            isDistributorView={isDistributorView}
          />
        </Box>
        )}

      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap:'12px' }}>
        <Box sx={{ flex: 1, gap:'10px' }} >
          {showTopFilterBar && (
          <TopFilterBar
            selectedReport={selectedReport}
            filters={filters}
            optionsMap={optionsMap}
            loadingMap={loadingMap}
            onFilterChange={handleFilterChange}
            onMultiFilterChange={handleMultiFilterChange}
            onSalesLevelChange={handleSalesLevelChange}
            onSalesValueChange={handleSalesValueChange}
            onSalesLoadLevels={loadSalesLevels}
            onSalesNextLevel={handleSalesNextLevel}
            onSalesResetAll={handleSalesResetAll}
            onSalesDropdownClose={handleSalesDropdownClose}
            onGeoLevelChange={handleGeographicalLevelChange}
            onGeoValueChange={handleGeographicalValueChange}
            onGeoLoadLevels={loadGeographicalLevels}
            onGeoNextLevel={handleGeoNextLevel}
            onGeoResetAll={handleGeoResetAll}
            onGeoDropdownClose={handleGeoDropdownClose}
            geoIsLastLevel={isGeoLastLevel}
            salesDrillDownPath={salesDrillDownPath}
            geoDrillDownPath={geoDrillDownPath}
            distributorSource={distributorSource}
            onDistributorSourceChange={handleDistributorSourceChange}
            onLoadDistributorOptions={loadDistributorOptions}
            searchTextMap={searchTextMap}
            onFilterInputChange={handleFilterInputChange}
            isHierarchySyncing={isHierarchySyncing}
            onReset={handleReset}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, ml: 2, alignItems: 'center' }}>
          {selectedReport?.isLiveReport !== true && selectedReport?.isPDFReport !== true && (
            <Button
              variant="outlined"
              onClick={handleCreateReport}
              disabled={isCreateReportDisabled}
              sx={{
                textTransform: "none",
                borderColor: "#00A392",
                color: "#00A392",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "14px",
                padding: "2px 8px",
                height: "33px",
                opacity: isCreateReportDisabled ? 0.5 : 1,
                cursor: isCreateReportDisabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  borderColor: "#00A392",
                  color: "#00A392",
                  backgroundColor: 'transparent'
                },
                '&.Mui-disabled': {
                  borderColor: '#ccc',
                  color: '#7c7c7c'
                }
              }}
            >
              Preview
            </Button>
          )}
          {downloadLoader ? (
            <div className="mdm-reports-download-loader">
              <Loader />
            </div>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={handleDownloadClick}
                disabled={isDownloadDisabled}
                startIcon={<Download sx={{ fontSize: '18px' }} />}
                sx={{
                  textTransform: "none",
                  background: "#00A392",
                  color: "#fff",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                  padding: "6px 12px",
                  height: "33px",
                  opacity: isDownloadDisabled ? 0.5 : 1,
                  cursor: isDownloadDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: "none",
                  '&:hover': {
                    background: "#00A392",
                    color: "#fff",
                    boxShadow: "none"
                  }
                }}
              >
                Download
              </Button>
              <Menu
                anchorEl={downloadAnchorEl}
                open={Boolean(downloadAnchorEl)}
                onClose={handleDownloadClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                  {selectedReport?.isPDFReport === true ? (
                    <MenuItem onClick={() => handleDownload('pdf')}>
                      <span>PDF</span>
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem onClick={() => handleDownload('csv')}>
                        <span>CSV</span>
                      </MenuItem>
                      <MenuItem onClick={() => handleDownload('xlsx')}>
                        <span>XLS</span>
                      </MenuItem>
                    </>
                  )}

              </Menu>
            </>
          )}
        </Box>
      </Box>
      {filterChips.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 1,
            mb: 0.3,
            px: 0.3
          }}
        >
          {filterChips.map((chip) => (
            <Chip
              key={chip.key}
              label={
                <span>
                  {chip.label}: <strong>{chip.value || "--"}</strong>
                  {chip.count !== undefined && chip.count > 0 && ` +${chip.count}`}
                </span>
              }
              onDelete={() => handleChipDelete(chip)}
              sx={{
                backgroundColor: "#E6FBF8",
                color: "#169C87",
                fontWeight: 500,
                fontSize: "13px",
                height: "28px",
                "& .MuiChip-label": {
                  px: 1.5
                },
                "& .MuiChip-deleteIcon": {
                  color: "#169C87",
                  fontSize: "18px",
                  "&:hover": {
                    color: "#0D7A6A"
                  }
                }
              }}
            />
          ))}
        </Box>
      )}
      <div className="mdm-reports-left-section" style={{ flex: 1, overflow: "auto", marginTop: "8px" }}>
        <MdmReportsPreview
          onBack={() => setShowPreview(false)}
          newReportConfig={selectedReport}
          filters={filters}
          dateRange={memoizedDateRange}
          salesDrillDownPath={salesDrillDownPath}
          geoDrillDownPath={geoDrillDownPath}
          optionsMap={optionsMap}
          loadingMap={loadingMap}
          onFilterChange={handleFilterChange}
          onMultiFilterChange={handleMultiFilterChange}
          onFilterOpen={handleFilterOpen}
          onFilterInputChange={handleFilterInputChange}
          onLoadDistributorOptions={loadDistributorOptions}
          searchTextMap={searchTextMap}
          customFilters={customFilters}
          showPreview={showPreview}
          mergedFilters={selectedReport?.mergedFilters}
          customFiltersLoading={customFiltersLoading}
        />
      </div>
    </div>
  );
}

